import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import MuiTab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { visuallyHidden } from '../../lib/visually-hidden';
import { cssVar } from '../../theme/tokens';
import { formatNumber } from '../../lib/numbers';

export interface TabItem<V extends string = string> {
  value: V;
  label: ReactNode;
  /** A route: the tab is a link (section tabs). Without it the tab calls `onChange`. */
  to?: string;
  disabled?: boolean;
  /** A count pill after the label (items in the tab); hidden while undefined (loading). */
  count?: number;
}

export interface TabsProps<V extends string = string> {
  /** Accessible name of the tab list. */
  label: string;
  items: readonly TabItem<V>[];
  /** The selected tab, or `false` when none matches. */
  value: V | false;
  onChange?: (value: V) => void;
  /** After the last tab, outside the tab list (the New badge of a count group). */
  trailing?: ReactNode;
}

function TabLabel({
  label,
  count,
  selected,
}: {
  label: ReactNode;
  count?: number;
  selected: boolean;
}) {
  if (count === undefined) return label;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      {label}
      {/* Read as "Videos, 24": the pill is drawn, the comma-separated count is announced. */}
      <span style={visuallyHidden}>, {formatNumber(count)}</span>
      <Box
        component="span"
        aria-hidden
        data-tab-count
        sx={{
          px: 1.75,
          borderRadius: cssVar('radius-pill'),
          typography: 'caption',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          bgcolor: selected ? cssVar('primary') : cssVar('track'),
          color: selected ? cssVar('text-on-primary') : cssVar('text-muted'),
        }}
      >
        {formatNumber(count)}
      </Box>
    </Box>
  );
}

/**
 * A row of tabs (scrolls sideways on narrow screens) with a gold underline on the selected tab;
 * tabs can be route links and can show a count.
 */
export function Tabs<V extends string = string>({
  label,
  items,
  value,
  onChange,
  trailing,
}: TabsProps<V>) {
  const tabSx = { minHeight: cssVar('control-h-md'), px: 0.5 };
  return (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', gap: 4, borderBottom: `1px solid ${cssVar('border')}` }}
    >
      <MuiTabs
        value={value}
        onChange={onChange ? (_event, next: V) => onChange(next) : undefined}
        aria-label={label}
        variant="scrollable"
        scrollButtons={false}
        sx={{
          minHeight: cssVar('control-h-md'),
          minWidth: 0,
          '& .MuiTabs-list': { gap: 7 },
        }}
      >
        {items.map((item) => {
          const content = (
            <TabLabel label={item.label} count={item.count} selected={item.value === value} />
          );
          return item.to ? (
            <MuiTab
              key={item.value}
              value={item.value}
              label={content}
              disabled={item.disabled}
              component={Link}
              to={item.to}
              sx={tabSx}
            />
          ) : (
            <MuiTab
              key={item.value}
              value={item.value}
              label={content}
              disabled={item.disabled}
              sx={tabSx}
            />
          );
        })}
      </MuiTabs>
      {trailing ? <Box sx={{ flex: 'none' }}>{trailing}</Box> : null}
    </Stack>
  );
}
